import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/constants';
import { FACULTY_LIST } from '../lib/constants';
import { t } from '../lib/theme';

export default function AuthPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode]       = useState('login');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]       = useState('');
  const [role, setRole]       = useState('fellow');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    setError(''); setSuccess(''); setLoading(true);
    if (mode === 'forgot') {
      if (!email) { setError('Please enter your email address.'); setLoading(false); return; }
      const { error: e } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/reset-password' });
      if (e) setError(e.message);
      else setSuccess('Password reset link sent! Please check your email inbox.');
      setLoading(false); return;
    }
    if (!email || !password || (mode === 'signup' && !name)) { setError('Please fill in all fields.'); setLoading(false); return; }
    if (mode === 'login') {
      const { error: e } = await signIn(email, password);
      if (e) setError('Incorrect email or password. Please try again.');
    } else {
      const { error: e } = await signUp(email, password, name, role);
      if (e) setError(e.message);
      else setSuccess('Account created! You can now sign in.');
    }
    setLoading(false);
  };

  const roles = [
    { id:'fellow',  icon:'👨‍⚕️', label:'Fellow',  desc:'Log and track your cases' },
    { id:'faculty', icon:'🩺',  label:'Faculty', desc:'Review and approve cases' },
  ];

  const switchMode = (m) => { setMode(m); setError(''); setSuccess(''); };

  return (
    <div style={s.page}>
      {/* Ambient background */}
      <div style={s.ambientLeft} />
      <div style={s.ambientRight} />

      <div style={s.wrap}>
        {/* Left panel */}
        <div style={s.left}>
          <div style={s.badge}>
            <div style={s.badgeDot}>K</div>
            <span style={s.badgeText}>King Hussein Cancer Center</span>
          </div>
          <h1 style={s.h1}>Hospice &amp; <em style={{ color:t.clay, fontStyle:'italic' }}>Palliative</em><br/>Medicine</h1>
          <div style={s.sub}>Fellowship Logbook · KHCC</div>
          <div style={s.divider} />
          <p style={s.quote}>Every case you log is a testament to your dedication and compassion. Let us capture your journey and celebrate your growth.</p>
          <div style={s.features}>
            {['Competency-based case logging','Faculty approval workflow','Quarterly evaluations & progress tracking','Academic activity records'].map((f,i) => (
              <div key={i} style={s.feat}><div style={s.featDot}/><span>{f}</span></div>
            ))}
          </div>
        </div>

        {/* Right card */}
        <div style={s.card}>
          <div style={s.stripe} />
          <div style={s.cardBody}>
            {mode !== 'forgot' && (
              <div style={s.tabs}>
                <button onClick={() => switchMode('login')} style={{ ...s.tab, ...(mode==='login'?s.tabActive:{}) }}>Sign In</button>
                <button onClick={() => switchMode('signup')} style={{ ...s.tab, ...(mode==='signup'?s.tabActive:{}) }}>Register</button>
              </div>
            )}
            {mode === 'forgot' && (
              <>
                <button onClick={() => switchMode('login')} style={s.backBtn}>← Back to Sign In</button>
                <div style={{ fontFamily:t.fonts.display, fontSize:20, color:t.slate, margin:'12px 0 4px' }}>Reset Your Password</div>
                <div style={{ fontSize:13, color:t.textL, lineHeight:1.6, marginBottom:16 }}>Enter your email and we'll send you a reset link.</div>
              </>
            )}
            {error   && <div style={s.errorBox}>{error}</div>}
            {success && <div style={s.successBox}>{success}</div>}

            {mode === 'signup' && (
              <>
                <div style={s.field}>
                  <label style={s.label}>Full Name</label>
                  {role === 'faculty'
                    ? <select style={s.input} value={name} onChange={e => setName(e.target.value)}>
                        <option value=''>— Select your name —</option>
                        {FACULTY_LIST.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    : <input style={s.input} placeholder="Dr. Jane Smith" value={name} onChange={e => setName(e.target.value)} />
                  }
                </div>
                <div style={s.field}>
                  <label style={s.label}>I am a…</label>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {roles.map(r => (
                      <button key={r.id} onClick={() => { setRole(r.id); setName(''); }}
                        style={{ ...s.roleBtn, ...(role===r.id?s.roleBtnActive:{}) }}>
                        <span style={{ fontSize:18 }}>{r.icon}</span>
                        <div>
                          <div style={{ fontWeight:600, fontSize:13 }}>{r.label}</div>
                          <div style={{ fontSize:11, opacity:0.65, marginTop:1 }}>{r.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input style={s.input} type="email" placeholder="you@khcc.jo" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            {mode !== 'forgot' && (
              <div style={s.field}>
                <label style={s.label}>Password</label>
                <input style={s.input} type="password" placeholder="••••••••" value={password}
                  onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key==='Enter'&&handleSubmit()} />
              </div>
            )}
            {mode === 'login' && (
              <div style={{ textAlign:'right', marginTop:-8, marginBottom:16 }}>
                <button onClick={() => switchMode('forgot')} style={s.forgotLink}>Forgot password?</button>
              </div>
            )}
            {!success && (
              <button style={{ ...s.submitBtn, opacity:loading?0.75:1 }} onClick={handleSubmit} disabled={loading}>
                {loading ? 'Please wait…' : mode==='login' ? 'Sign In →' : mode==='signup' ? 'Create Account' : 'Send Reset Link'}
              </button>
            )}
            {mode !== 'forgot' && (
              <div style={s.switchRow}>
                {mode==='login'
                  ? <span>New here? <button style={s.link} onClick={() => switchMode('signup')}>Register now</button></span>
                  : <span>Have an account? <button style={s.link} onClick={() => switchMode('login')}>Sign in</button></span>}
              </div>
            )}
            {mode==='forgot' && success && (
              <button onClick={() => switchMode('login')} style={{ ...s.submitBtn, marginTop:12 }}>Back to Sign In</button>
            )}
          </div>
          <div style={s.cardFooter}>King Hussein Cancer Center · HPM Fellowship · Amman, Jordan</div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:         { minHeight:'100vh', background:`linear-gradient(135deg, ${t.slate} 0%, #4A6070 40%, #3A5248 100%)`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:t.fonts.body, padding:'32px 20px', position:'relative', overflow:'hidden' },
  ambientLeft:  { position:'absolute', inset:0, background:'radial-gradient(ellipse at 28% 72%, rgba(200,149,108,0.14) 0%, transparent 55%)', pointerEvents:'none' },
  ambientRight: { position:'absolute', inset:0, background:'radial-gradient(ellipse at 75% 18%, rgba(74,158,137,0.11) 0%, transparent 50%)', pointerEvents:'none' },
  wrap:         { position:'relative', zIndex:2, width:'100%', maxWidth:980, display:'flex', gap:48, alignItems:'center' },
  left:         { flex:1 },
  badge:        { display:'inline-flex', alignItems:'center', gap:8, background:'rgba(200,149,108,0.14)', border:'1px solid rgba(200,149,108,0.28)', borderRadius:40, padding:'5px 14px 5px 6px', marginBottom:26 },
  badgeDot:     { width:26, height:26, borderRadius:'50%', background:t.clay, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff' },
  badgeText:    { fontSize:11, color:'rgba(200,149,108,0.9)', letterSpacing:'1px', textTransform:'uppercase', fontWeight:500 },
  h1:           { fontFamily:t.fonts.display, fontSize:44, fontWeight:400, color:'#F5EFE6', lineHeight:1.15, marginBottom:8 },
  sub:          { fontSize:17, color:'rgba(200,149,108,0.8)', marginBottom:22, fontFamily:t.fonts.display },
  divider:      { width:48, height:2, background:`linear-gradient(to right, ${t.clay}, transparent)`, marginBottom:20 },
  quote:        { fontSize:14, color:'rgba(245,239,230,0.6)', lineHeight:1.9, fontStyle:'italic', marginBottom:28, maxWidth:380 },
  features:     { display:'flex', flexDirection:'column', gap:10 },
  feat:         { display:'flex', alignItems:'center', gap:10, fontSize:13, color:'rgba(245,239,230,0.6)' },
  featDot:      { width:6, height:6, borderRadius:'50%', background:t.clay, flexShrink:0 },
  card:         { width:375, flexShrink:0, background:'rgba(255,255,255,0.97)', borderRadius:18, overflow:'hidden', boxShadow:'0 24px 60px rgba(0,0,0,0.25)' },
  stripe:       { height:4, background:`linear-gradient(to right, ${t.teal}, ${t.clay})` },
  cardBody:     { padding:'24px 26px' },
  cardFooter:   { padding:'11px 20px', background:t.warm, borderTop:`1px solid rgba(200,149,108,0.15)`, textAlign:'center', fontSize:10, color:t.textL, letterSpacing:'0.5px' },
  tabs:         { display:'flex', background:t.warm, borderRadius:9, padding:3, marginBottom:18 },
  tab:          { flex:1, padding:'8px', border:'none', background:'transparent', fontFamily:t.fonts.body, fontSize:13, fontWeight:500, color:t.textL, cursor:'pointer', borderRadius:7 },
  tabActive:    { background:'#fff', color:t.slate, fontWeight:700, boxShadow:'0 1px 6px rgba(60,50,40,0.12)' },
  backBtn:      { background:'none', border:'none', color:t.teal, fontSize:13, fontWeight:600, cursor:'pointer', padding:0, fontFamily:t.fonts.body },
  field:        { marginBottom:14 },
  label:        { display:'block', fontSize:10, fontWeight:700, color:t.textL, letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:5 },
  input:        { width:'100%', padding:'11px 13px', border:`1.5px solid rgba(200,149,108,0.25)`, borderRadius:9, fontFamily:t.fonts.body, fontSize:14, color:t.text, background:t.sand, outline:'none', boxSizing:'border-box' },
  roleBtn:      { width:'100%', padding:'10px 14px', border:`1.5px solid rgba(200,149,108,0.2)`, borderRadius:9, background:'transparent', textAlign:'left', cursor:'pointer', fontFamily:t.fonts.body, color:t.text, display:'flex', alignItems:'center', gap:12, transition:'all 0.15s' },
  roleBtnActive:{ background:t.slate, color:'#F5EFE6', borderColor:t.slate },
  forgotLink:   { background:'none', border:'none', color:t.teal, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:t.fonts.body, padding:0 },
  submitBtn:    { width:'100%', padding:'12px', background:`linear-gradient(135deg, ${t.teal}, ${t.tealL})`, color:'#fff', border:'none', borderRadius:9, fontFamily:t.fonts.body, fontSize:14, fontWeight:600, cursor:'pointer', marginBottom:12, letterSpacing:'0.3px' },
  switchRow:    { textAlign:'center', fontSize:12, color:t.textL },
  link:         { background:'none', border:'none', color:t.teal, fontWeight:600, cursor:'pointer', fontSize:12, fontFamily:t.fonts.body, padding:0 },
  errorBox:     { background:'#FFF0F0', border:'1px solid #F4A8A8', color:'#8A2D2D', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 },
  successBox:   { background:'#F0FFF6', border:'1px solid #A8DCC0', color:'#1E5C40', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:14 },
};
